<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PlatformIcon from '@/Components/Public/PlatformIcon.vue'

// The website chat, our own panel (2026-09-28). Chatwoot's SDK widget took
// the whole screen on a phone and could not be styled; this one is a small
// window on every device and talks to Public::ChatSessionsController, which
// keeps the conversation in the same Chatwoot inbox (CRM, Telegram nudge and
// the AI concierge all unchanged). Messages are polled: 4 s while the panel
// is open, 30 s while it is closed with a session, never while the tab is
// hidden. The session itself lives in an encrypted, HttpOnly cookie; the
// browser only remembers that it has one.
const { t, locale } = useI18n()
const page = usePage()

const SESSION_FLAG = 'gp_chat_session'
const CACHE_KEY    = 'gp_chat_cache'
const POLL_OPEN    = 4000
const POLL_CLOSED  = 30000

const open       = ref(false)
const messages   = ref([])
const draft      = ref('')
const name       = ref('')
const sending    = ref(false)
const booting    = ref(false)
const error      = ref('')      // '', 'unavailable', 'rate_limited', 'send'
const unread     = ref(false)
const hasSession = ref(false)
const composer   = ref(null)
const list       = ref(null)
const panel      = ref(null)
let pollTimer = null
let vvCleanup = null

const hours = computed(() => page.props.trust?.hours || '09:00–22:00')
const whatsapp = computed(() => {
  const groups = page.props.brand?.channels || {}
  const all = [ ...(groups.message || []), ...(groups.support || []) ]
  return all.find(c => c.platform === 'whatsapp')?.url || null
})
const lastId = computed(() => messages.value.reduce((m, x) => (typeof x.id === 'number' && x.id > m ? x.id : m), 0))
const quick = [ 'boat', 'villa', 'transfer' ]

const base = () => (typeof window !== 'undefined' && window.location.pathname.startsWith('/_p') ? '/_p' : '')

function storage(fn) { try { return fn(window.localStorage) } catch { return null } }
function remember() { storage(s => { s.setItem(SESSION_FLAG, '1'); s.setItem(CACHE_KEY, JSON.stringify(messages.value.filter(m => !m.pending).slice(-50))) }) }
function forget() { storage(s => { s.removeItem(SESSION_FLAG); s.removeItem(CACHE_KEY) }) }

// The page's CSRF token: the endpoints are cookie-authenticated, so a
// write without it is refused (422 "csrf") rather than silently degraded.
function csrfToken() {
  return typeof document !== 'undefined' ? (document.querySelector('meta[name="csrf-token"]')?.content || '') : ''
}

async function api(method, path, body) {
  try {
    const r = await fetch(`${base()}${path}`, {
      method, credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: body ? JSON.stringify(body) : undefined
    })
    const json = await r.json().catch(() => ({}))
    return { status: r.status, ...json }
  } catch {
    return { status: 0, ok: false, error: 'unavailable' }
  }
}

function merge(incoming) {
  if (!incoming?.length) return
  const byId = new Map(messages.value.filter(m => typeof m.id === 'number').map(m => [ m.id, m ]))
  let added = false
  for (const m of incoming) {
    if (byId.has(m.id)) continue
    // Our own message may come back from a poll before the send resolved:
    // adopt the pending bubble instead of showing it twice.
    const pending = messages.value.find(x => x.pending && x.from === 'visitor' && x.text === m.text)
    if (pending) { Object.assign(pending, m, { pending: false }); continue }
    messages.value.push({ ...m, pending: false })
    added = true
    if (m.from === 'concierge' && !open.value) unread.value = true
  }
  if (added) { messages.value.sort((a, b) => (a.id || 0) - (b.id || 0)); remember(); scrollDown() }
}

async function poll() {
  if (!hasSession.value || (typeof document !== 'undefined' && document.hidden)) return
  const r = await api('GET', `/chat/messages${lastId.value ? `?after=${lastId.value}` : ''}`)
  if (r.status === 401) { hasSession.value = false; forget(); return }
  if (r.ok) merge(r.messages)
}

function schedule() {
  clearTimeout(pollTimer)
  if (!hasSession.value) return
  pollTimer = setTimeout(async () => { await poll(); schedule() }, open.value ? POLL_OPEN : POLL_CLOSED)
}

async function ensureSession() {
  if (hasSession.value) return true
  booting.value = true
  const r = await api('POST', '/chat', { name: name.value.trim(), page: typeof window !== 'undefined' ? window.location.pathname : '' })
  booting.value = false
  if (!r.ok) { error.value = 'unavailable'; return false }
  hasSession.value = true
  merge(r.messages || [])
  remember()
  schedule()
  return true
}

async function send() {
  const text = draft.value.trim()
  if (!text || sending.value) return
  error.value = ''
  sending.value = true
  if (!(await ensureSession())) { sending.value = false; return }
  const echo = `w${Date.now()}`
  const bubble = { id: echo, from: 'visitor', text, at: new Date().toISOString(), pending: true }
  messages.value.push(bubble)
  draft.value = ''
  scrollDown()
  const r = await api('POST', '/chat/messages', { content: text, echo_id: echo })
  sending.value = false
  if (r.ok && r.message) {
    Object.assign(bubble, r.message, { pending: false })
    remember()
  } else {
    messages.value = messages.value.filter(m => m.id !== bubble.id)
    draft.value = text
    if (r.status === 401) { hasSession.value = false; forget(); error.value = 'send' }
    else error.value = r.error === 'rate_limited' ? 'rate_limited' : (r.status === 503 || r.status === 0 ? 'unavailable' : 'send')
  }
  focusComposer()
}

function onKey(e) {
  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
}

function useQuick(key) {
  draft.value = t(`public.widget.quick_text.${key}`)
  focusComposer()
}

async function restart() {
  await api('DELETE', '/chat')
  hasSession.value = false
  messages.value = []
  error.value = ''
  forget()
  clearTimeout(pollTimer)
  focusComposer()
}

function toggle() { open.value ? close() : show() }
function show() {
  open.value = true
  unread.value = false
  if (hasSession.value && messages.value.length === 0) storage(s => { const c = s.getItem(CACHE_KEY); if (c) messages.value = JSON.parse(c) })
  poll().then(schedule)
  nextTick(() => { scrollDown(); focusComposer() })
}
function close() { open.value = false; schedule() }
function onEsc(e) { if (e.key === 'Escape' && open.value) close() }

function focusComposer() { nextTick(() => composer.value?.focus({ preventScroll: true })) }
function scrollDown() { nextTick(() => { if (list.value) list.value.scrollTop = list.value.scrollHeight }) }
function grow(e) { const el = e.target; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 132)}px` }

function timeOf(iso) {
  try { return new Intl.DateTimeFormat(locale.value || 'en', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso)) } catch { return '' }
}

// On a phone the keyboard shrinks the visual viewport but not the layout
// viewport; the sheet follows the visual one so the composer stays visible.
function bindViewport() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  if (!vv) return
  const apply = () => {
    if (!panel.value) return
    const gap = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop))
    panel.value.style.setProperty('--kb', `${Math.round(gap)}px`)
    panel.value.style.setProperty('--vvh', `${Math.round(vv.height)}px`)
  }
  vv.addEventListener('resize', apply); vv.addEventListener('scroll', apply); apply()
  vvCleanup = () => { vv.removeEventListener('resize', apply); vv.removeEventListener('scroll', apply) }
}

watch(open, (v) => { if (v) bindViewport(); else { vvCleanup?.(); vvCleanup = null } })

onMounted(() => {
  hasSession.value = !!storage(s => s.getItem(SESSION_FLAG))
  if (hasSession.value) { storage(s => { const c = s.getItem(CACHE_KEY); if (c) messages.value = JSON.parse(c) }); schedule() }
  window.addEventListener('keydown', onEsc)
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { poll(); schedule() } })
})
onBeforeUnmount(() => { clearTimeout(pollTimer); vvCleanup?.(); window.removeEventListener('keydown', onEsc) })
</script>

<template>
  <div class="chat">
    <button v-show="!open" type="button" class="chat__launcher" :class="{ 'chat__launcher--unread': unread }" :aria-label="t('public.widget.cta')" :aria-expanded="open ? 'true' : 'false'" aria-controls="gp-chat-panel" @click="show">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
      </svg>
      <span class="chat__launcher-label">{{ unread ? t('public.widget.unread') : t('public.widget.cta') }}</span>
      <i v-if="unread" class="chat__dot" aria-hidden="true" />
    </button>

    <section v-show="open" id="gp-chat-panel" ref="panel" class="chat__panel" role="dialog" :aria-label="t('public.widget.title')">
      <header class="chat__head">
        <div class="chat__head-text">
          <p class="chat__title">{{ t('public.widget.title') }}</p>
          <p class="chat__sub">{{ t('public.widget.sub', { hours }) }}</p>
        </div>
        <button v-if="hasSession" type="button" class="chat__restart" @click="restart">{{ t('public.widget.restart') }}</button>
        <button type="button" class="chat__close" :aria-label="t('public.widget.close')" @click="close">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </header>

      <div ref="list" class="chat__list" aria-live="polite">
        <div class="chat__msg chat__msg--concierge">
          <p class="chat__bubble">{{ t('public.widget.welcome') }}</p>
        </div>
        <div v-if="messages.length === 0" class="chat__quick">
          <button v-for="q in quick" :key="q" type="button" class="chat__chip" @click="useQuick(q)">{{ t(`public.widget.quick.${q}`) }}</button>
          <a :href="`${base()}/plan`" class="chat__chip chat__chip--link">{{ t('public.widget.quick.plan') }}</a>
        </div>
        <div v-for="m in messages" :key="m.id" class="chat__msg" :class="m.from === 'visitor' ? 'chat__msg--me' : 'chat__msg--concierge'">
          <p v-if="m.from !== 'visitor' && m.sender" class="chat__who">{{ m.sender }}</p>
          <p class="chat__bubble" :class="{ 'is-pending': m.pending }">{{ m.text }}</p>
          <time class="chat__time" :datetime="m.at">{{ timeOf(m.at) }}</time>
        </div>
        <p v-if="hasSession && messages.length > 0 && messages.every(m => m.from === 'visitor')" class="chat__hint">{{ t('public.widget.hint_contact') }}</p>
        <p v-if="error" class="chat__error" role="alert">
          {{ t(`public.widget.errors.${error}`) }}
          <a v-if="whatsapp && error === 'unavailable'" :href="whatsapp" target="_blank" rel="noopener" class="chat__error-link"><PlatformIcon platform="whatsapp" size="0.9rem" /> {{ t('public.widget.fallback_whatsapp') }}</a>
        </p>
      </div>

      <form class="chat__composer" @submit.prevent="send">
        <input v-if="!hasSession" v-model="name" type="text" class="chat__name" maxlength="80" autocomplete="name" :placeholder="t('public.widget.name_placeholder')" :aria-label="t('public.widget.name_label')">
        <div class="chat__row">
          <textarea ref="composer" v-model="draft" class="chat__input" rows="1" maxlength="2000" :placeholder="t('public.widget.placeholder')" :aria-label="t('public.widget.placeholder')" @keydown="onKey" @input="grow" />
          <button type="submit" class="chat__send" :disabled="sending || booting || !draft.trim()" :aria-label="t('public.widget.send')">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
          </button>
        </div>
        <p class="chat__foot">Get Phuket Co., Ltd.</p>
      </form>
    </section>
  </div>
</template>

<style scoped>
/* Launcher: same voice as LuxButton (dark tone, solid) — a concierge bell. */
.chat__launcher {
  position: fixed; right: 1.25rem; bottom: max(env(safe-area-inset-bottom), 1.25rem); z-index: 60;
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.85rem 1.3rem 0.85rem 1.1rem;
  background: var(--night, #0B2537); color: var(--foam, #ECF3F6);
  border: 1px solid color-mix(in oklch, var(--gold, #D8B46B) 65%, transparent); border-radius: 999px;
  font-family: var(--font-body, var(--body)); font-weight: 600; font-size: 0.74rem;
  letter-spacing: 0.14em; text-transform: uppercase; line-height: 1; cursor: pointer;
  box-shadow: 0 18px 40px -20px rgba(0, 0, 0, 0.7), inset 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 25%, transparent);
  transition: transform 150ms ease, border-color 150ms ease, background 150ms ease;
}
.chat__launcher:hover { transform: translateY(-1px); border-color: var(--gold, #D8B46B); background: var(--night2, #123047); }
.chat__dot { position: absolute; top: -0.2rem; right: -0.2rem; width: 0.8rem; height: 0.8rem; border-radius: 50%; background: var(--accent); border: 2px solid var(--surface); }
/* Icon-only at rest on every screen (the pill covered card titles and links
   in the bottom-right corner on desktop — 2026-10-29 review); the label
   unfolds on hover/focus, and stays out while a reply is waiting. */
.chat__launcher { padding: 0.7rem; }
.chat__launcher-label { display: none; }
.chat__launcher:hover, .chat__launcher:focus-visible, .chat__launcher--unread { padding: 0.85rem 1.3rem 0.85rem 1.1rem; }
.chat__launcher:hover .chat__launcher-label, .chat__launcher:focus-visible .chat__launcher-label, .chat__launcher--unread .chat__launcher-label { display: inline; }
/* Pages with the sticky phone price bar (MobileBar, 66px): the launcher used to
   sit ON the bar and cover the end of its "Request this boat" button
   (2026-10-29 review, every boat page on a phone). Lift it above the bar. */
body:has(.mbar:not(.mbar--hidden)) .chat__launcher {
  bottom: calc(66px + 0.75rem + env(safe-area-inset-bottom, 0px));
}

/* Panel: a small window, bottom-right on desktop; a bottom sheet on a phone
   that follows the visual viewport when the keyboard opens (--kb, --vvh). */
.chat__panel {
  position: fixed; z-index: 70; right: 1.25rem; bottom: calc(env(safe-area-inset-bottom, 0px) + 1.25rem);
  width: min(24rem, calc(100vw - 2rem)); height: min(38rem, calc(100vh - 3rem));
  display: flex; flex-direction: column; overflow: hidden;
  background: var(--surface); color: var(--ink);
  border: 1px solid var(--line); border-radius: 1.25rem;
  box-shadow: 0 30px 70px -30px rgba(0, 0, 0, 0.55), 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 18%, transparent);
}
@media (max-width: 640px) {
  .chat__panel {
    right: 0; left: 0; width: 100%;
    bottom: var(--kb, 0px);
    height: min(72vh, 36rem); max-height: calc(var(--vvh, 100vh) - 0.5rem);
    border-radius: 1.25rem 1.25rem 0 0; border-bottom: 0;
  }
}
.chat__head { display: flex; align-items: flex-start; gap: 0.6rem; padding: 0.95rem 1rem 0.85rem 1.1rem; background: var(--night, #0B2537); color: var(--foam, #ECF3F6); border-bottom: 1px solid color-mix(in oklch, var(--gold, #D8B46B) 45%, transparent); }
.chat__head-text { flex: 1; min-width: 0; }
.chat__title { margin: 0; font-family: var(--font-display, var(--display)); font-size: 1.1rem; line-height: 1.2; }
.chat__sub { margin: 0.2rem 0 0; font-size: 0.78rem; line-height: 1.35; opacity: 0.85; }
.chat__restart { align-self: center; background: transparent; border: 0; color: inherit; opacity: 0.75; font: inherit; font-size: 0.72rem; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; padding: 0.3rem 0.4rem; }
.chat__restart:hover { opacity: 1; }
.chat__close { flex: none; width: 2.1rem; height: 2.1rem; display: grid; place-items: center; border-radius: 50%; border: 1px solid color-mix(in oklch, var(--foam, #ECF3F6) 25%, transparent); background: transparent; color: inherit; cursor: pointer; }
.chat__close:hover { background: color-mix(in oklch, var(--foam, #ECF3F6) 12%, transparent); }

.chat__list { flex: 1; overflow-y: auto; padding: 1rem 1rem 0.5rem; display: flex; flex-direction: column; gap: 0.55rem; background: var(--bg); overscroll-behavior: contain; }
.chat__msg { display: flex; flex-direction: column; max-width: 84%; }
.chat__msg--concierge { align-self: flex-start; align-items: flex-start; }
.chat__msg--me { align-self: flex-end; align-items: flex-end; }
.chat__who { margin: 0 0 0.15rem 0.3rem; font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted); }
.chat__bubble { margin: 0; padding: 0.6rem 0.85rem; border-radius: 1rem; line-height: 1.45; font-size: 0.95rem; white-space: pre-wrap; overflow-wrap: anywhere; }
.chat__msg--concierge .chat__bubble { background: var(--surface); color: var(--ink); border: 1px solid var(--line); border-bottom-left-radius: 0.35rem; }
.chat__msg--me .chat__bubble { background: var(--night, #0B2537); color: var(--foam, #ECF3F6); border-bottom-right-radius: 0.35rem; }
.chat__bubble.is-pending { opacity: 0.6; }
.chat__time { margin: 0.15rem 0.3rem 0; font-size: 0.68rem; color: var(--muted); }
.chat__quick { display: flex; flex-wrap: wrap; gap: 0.4rem; margin: 0.2rem 0 0.4rem; }
.chat__chip { padding: 0.4rem 0.8rem; border-radius: 999px; border: 1px solid var(--line); background: var(--surface); color: var(--ink); font: inherit; font-size: 0.82rem; font-weight: 600; cursor: pointer; text-decoration: none; }
.chat__chip:hover { border-color: color-mix(in oklch, var(--gold, #D8B46B) 70%, var(--line)); }
.chat__hint { margin: 0.2rem 0 0; font-size: 0.78rem; color: var(--muted); line-height: 1.4; }
.chat__error { margin: 0.2rem 0 0; font-size: 0.82rem; color: var(--danger); line-height: 1.4; }
.chat__error-link { display: inline-flex; align-items: center; gap: 0.3rem; margin-left: 0.4rem; color: var(--ink); font-weight: 600; }

.chat__composer { padding: 0.6rem 0.75rem calc(env(safe-area-inset-bottom, 0px) + 0.55rem); border-top: 1px solid var(--line); background: var(--surface); display: grid; gap: 0.45rem; }
.chat__name { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid var(--line); border-radius: 0.75rem; background: var(--bg); color: var(--ink); font: inherit; font-size: 0.9rem; }
.chat__row { display: flex; align-items: flex-end; gap: 0.5rem; }
.chat__input { flex: 1; min-height: 2.6rem; max-height: 8.25rem; padding: 0.6rem 0.8rem; border: 1px solid var(--line); border-radius: 1rem; background: var(--bg); color: var(--ink); font: inherit; font-size: 16px; line-height: 1.4; resize: none; }
.chat__input:focus-visible, .chat__name:focus-visible { outline: 2px solid var(--second); outline-offset: 1px; }
.chat__send { flex: none; width: 2.6rem; height: 2.6rem; display: grid; place-items: center; border-radius: 50%; border: 0; background: var(--night, #0B2537); color: var(--gold, #D8B46B); cursor: pointer; }
.chat__send:disabled { opacity: 0.4; cursor: default; }
.chat__foot { margin: 0; text-align: center; font-size: 0.66rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); }
@media (prefers-reduced-motion: reduce) { .chat__launcher { transition: none; } }
</style>
